import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import Alert from '../components/common/Alert';
import Pagination from '../components/common/Pagination';
import ProductFormModal from '../components/products/ProductFormModal';
import ProductCard from '../components/products/ProductCard';
import ViewToggle from '../components/products/ViewToggle';
import { useAuth } from '../contexts/AuthContext';
import {
  getAllProducts,
  updateProduct,
  deleteProduct,
  type ProductWithStores,
} from '../services/productService';
import { extractApiError } from '../utils/errorUtils';
import { ViewMode, UpdateProductPayload, CreateProductPayload } from '../types/product';
import styles from './AllProductsPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import layoutStyles from '../styles/layout.module.css';
import placeholderImage from '../assets/placeholder.png';

const DEFAULT_LIMIT = 20;

const AllProductsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';

  const [products, setProducts] = useState<ProductWithStores[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProductWithStores | null>(null);

  const fetchProducts = useCallback(async () => {
    if (!isAdmin) return;
    setIsLoading(true);
    try {
      const data = await getAllProducts({
        page,
        limit,
        search: search || undefined,
        sortBy,
        sortOrder,
      });
      setProducts(data.items);
      setTotal(data.total);
    } catch (err) {
      setProducts([]);
      setTotal(0);
      setAlert({ message: extractApiError(err, 'Failed to load products.'), type: 'error', isVisible: true });
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, page, limit, search, sortBy, sortOrder]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const handlePageChange = (newPage: number) => setPage(newPage);
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  useEffect(() => {
    if (alert.isVisible) {
      const t = setTimeout(() => setAlert((a) => ({ ...a, isVisible: false })), 3000);
      return () => clearTimeout(t);
    }
  }, [alert.isVisible]);

  const hideAlert = () => setAlert((a) => ({ ...a, isVisible: false }));
  const showAlert = (message: string, type: 'success' | 'error') =>
    setAlert({ message, type, isVisible: true });

  const firstStoreId = editingProduct?.stores?.[0]?.id;

  const handleEdit = (product: ProductWithStores) => {
    setEditingProduct(product);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleDelete = async (productId: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    hideAlert();
    try {
      await deleteProduct(productId);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      setTotal((t) => Math.max(0, t - 1));
      showAlert('Product deleted successfully.', 'success');
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to delete product.'), 'error');
    }
  };

  const handleFormSubmit = async (productData: CreateProductPayload | UpdateProductPayload, imageFile?: File) => {
    if (!editingProduct) return;
    hideAlert();
    setIsSubmitting(true);
    try {
      const payload: UpdateProductPayload = {
        product_name: productData.product_name ?? editingProduct.product_name,
        product_description: productData.product_description ?? editingProduct.product_description ?? editingProduct.brief ?? '',
        price: productData.price ?? editingProduct.price,
        image_url: productData.image_url !== undefined ? productData.image_url : editingProduct.image_url,
        sector_id: productData.sector_id !== undefined ? productData.sector_id : editingProduct.sector_id ?? null,
      };
      let updated = await updateProduct(editingProduct.id, payload, imageFile, firstStoreId);

      const merged: ProductWithStores = {
        ...updated.product,
        stores: editingProduct.stores,
        name: updated.product.product_name,
        description: updated.product.product_description,
      };
      setProducts((prev) => prev.map((p) => (p.id === editingProduct.id ? merged : p)));
      showAlert('Product updated successfully.', 'success');
      setIsModalOpen(false);
      setEditingProduct(null);
    } catch (err) {
      showAlert(extractApiError(err, 'Failed to update product.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.errorMessage}>Access denied. Admin only.</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && products.length === 0) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p className={styles.loadingMessage}>Loading products...</p>
          </main>
        </div>
      </div>
    );
  }

  const displayProducts = products.map((p) => ({
    ...p,
    name: p.name ?? p.product_name,
    description: p.description ?? p.product_description,
  }));

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert message={alert.message} type={alert.type} isVisible={alert.isVisible} onClose={hideAlert} />
          <div className={styles.titleBar}>
            <h2 className={styles.title}>All Products</h2>
            <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
          </div>
          <div className={styles.toolbar}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Search products..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={styles.searchInput}
              />
              <button type="submit" className={styles.searchButton}>Search</button>
            </form>
            <label className={styles.sortLabel}>
              Sort by:
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [by, order] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                  setSortBy(by);
                  setSortOrder(order);
                  setPage(1);
                }}
                className={styles.sortSelect}
              >
                <option value="id-asc">ID (asc)</option>
                <option value="id-desc">ID (desc)</option>
                <option value="product_name-asc">Name (A–Z)</option>
                <option value="product_name-desc">Name (Z–A)</option>
                <option value="price-asc">Price (low–high)</option>
                <option value="price-desc">Price (high–low)</option>
              </select>
            </label>
          </div>
          {products.length === 0 && !isLoading ? (
            <p className={styles.emptyState}>No products found across all stores.</p>
          ) : viewMode === 'table' ? (
            <div className={styles.tableContainer}>
              <table className={tableStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Name</th>
                    <th>Sector</th>
                    <th>Stores</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayProducts.map((product) => (
                    <tr key={product.id}>
                      <td className={tableStyles.imageCell}>
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.product_name}
                            className={tableStyles.tableImage}
                            onError={(e) => { e.currentTarget.src = placeholderImage; }}
                          />
                        ) : (
                          <div className={tableStyles.tablePlaceholder}>📦</div>
                        )}
                      </td>
                      <td>{product.product_name}</td>
                      <td>{product.sector?.sector_name ?? '—'}</td>
                      <td className={styles.storesCell}>
                        {product.stores?.length
                          ? product.stores.map((st) => (
                              <React.Fragment key={st.id}>
                                <Link to={`/store/${st.id}/overview`} className={styles.storeLink}>{st.store_name}</Link>
                                {st !== product.stores![product.stores!.length - 1] ? ', ' : null}
                              </React.Fragment>
                            ))
                          : '—'}
                      </td>
                      <td>{product.price != null ? `$${Number(product.price).toFixed(2)}` : '—'}</td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button
                            type="button"
                            onClick={() => handleEdit(product)}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            disabled={!product.stores?.length}
                            title={!product.stores?.length ? 'No store linked' : 'Edit'}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(product.id)}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={cardGridStyles.cardsGrid}>
              {displayProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onEdit={() => handleEdit(product)}
                  onDelete={() => handleDelete(product.id)}
                  canEdit
                />
              ))}
            </div>
          )}
          {total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 20, 50]}
              showLimitSelector
              disabled={isLoading}
            />
          )}
          <ProductFormModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setEditingProduct(null); hideAlert(); }}
            onSubmit={handleFormSubmit}
            initialData={editingProduct ? { ...editingProduct, name: editingProduct.product_name, description: editingProduct.product_description ?? editingProduct.brief } : null}
            isLoading={isSubmitting}
            selectedSectorId={editingProduct?.sector_id ?? null}
            storeId={firstStoreId}
          />
        </main>
      </div>
    </div>
  );
};

export default AllProductsPage;
